import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import Navbar from '../components/Navbar';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { CheckCircle, XCircle, Trash2, Clock, MapPin, Star, ArrowRight, Eye } from 'lucide-react';
import { toast } from 'sonner';

const AdminCoachesPage = () => {
  const { API, user } = useContext(AuthContext);
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pending, approved, rejected

  const isAdmin = ['shafirroy@icloud.com', 'shafirroy@gmail.com'].includes(user?.email);

  useEffect(() => {
    if (isAdmin) fetchCoaches();
  }, []);

  const fetchCoaches = async () => {
    try {
      const response = await axios.get(`${API}/coaches/all`);
      setCoaches(response.data);
    } catch (error) {
      toast.error('שגיאה בטעינת רשימת המאמנים');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (coachId, status) => {
    try {
      await axios.put(`${API}/admin/coaches/${coachId}/status?status=${status}`);
      toast.success(`סטטוס עודכן ל: ${status === 'approved' ? 'מאושר' : status === 'rejected' ? 'נדחה' : 'ממתין'}`);
      fetchCoaches();
    } catch (error) {
      toast.error('שגיאה בעדכון סטטוס');
    }
  };

  const deleteCoach = async (coachId, name) => {
    if (!window.confirm(`למחוק את המאמן ${name}?`)) return;
    try {
      await axios.delete(`${API}/admin/coaches/${coachId}`);
      toast.success('המאמן נמחק');
      fetchCoaches();
    } catch (error) {
      toast.error('שגיאה במחיקה');
    }
  };

  const filteredCoaches = filter === 'all' ? coaches : coaches.filter(c => c.status === filter);

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      approved: 'bg-green-100 text-green-800 border-green-300',
      rejected: 'bg-red-100 text-red-800 border-red-300',
    };
    const labels = { pending: 'ממתין לאישור', approved: 'מאושר', rejected: 'נדחה' };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-bold border ${styles[status] || styles.pending}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    return parts.length > 1 ? `${parts[0][0]}${parts[1][0]}` : name.substring(0, 2);
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
        <Navbar />
        <div className="text-center py-20">
          <h2 className="text-2xl font-bold text-gray-700">גישה מוגבלת - אדמין בלבד</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      <Navbar />

      <div className="max-w-5xl mx-auto px-4 py-8">
        <Link to="/coaches" className="inline-flex items-center gap-2 text-gray-500 hover:text-emerald-600 transition-colors mb-6 group">
          <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          <span className="font-medium">חזרה לדף המאמנים</span>
        </Link>

        <h1 className="text-3xl font-bold text-gray-900 mb-6" data-testid="admin-coaches-title">ניהול מאמנים</h1>

        {/* Filter Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          {[
            { id: 'all', label: 'הכל', count: coaches.length },
            { id: 'pending', label: 'ממתינים', count: coaches.filter(c => c.status === 'pending').length },
            { id: 'approved', label: 'מאושרים', count: coaches.filter(c => c.status === 'approved').length },
            { id: 'rejected', label: 'נדחים', count: coaches.filter(c => c.status === 'rejected').length },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setFilter(tab.id)}
              className={`px-4 py-2 rounded-xl text-sm font-bold transition-all ${
                filter === tab.id
                  ? 'bg-emerald-500 text-white shadow-md'
                  : 'bg-white text-gray-600 border-2 border-gray-200 hover:bg-gray-50'
              }`}
              data-testid={`filter-${tab.id}`}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-10 text-gray-500">טוען...</div>
        ) : filteredCoaches.length === 0 ? (
          <div className="text-center py-10 text-gray-500">אין מאמנים בקטגוריה זו</div>
        ) : (
          <div className="space-y-4" data-testid="admin-coaches-list">
            {filteredCoaches.map(coach => (
              <Card key={coach.id} className="border-2 border-gray-100 shadow-sm hover:shadow-md transition-all" data-testid={`admin-coach-${coach.id}`}>
                <CardContent className="p-5">
                  <div className="flex flex-col sm:flex-row items-start gap-4">
                    <Avatar className="h-16 w-16 ring-2 ring-gray-200">
                      <AvatarImage src={coach.image} />
                      <AvatarFallback className="bg-gradient-to-br from-emerald-400 to-teal-600 text-white font-bold">
                        {getInitials(coach.full_name)}
                      </AvatarFallback>
                    </Avatar>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 flex-wrap">
                        <h3 className="text-lg font-bold text-gray-900">{coach.full_name}</h3>
                        {getStatusBadge(coach.status)}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                        <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{coach.city} | {coach.area}</span>
                        <span>{coach.phone}</span>
                        <span>{coach.experience_years} שנות ניסיון</span>
                        {coach.club && <span className="text-teal-600 font-medium">{coach.club}</span>}
                        <span className="flex items-center gap-1 text-blue-500">
                          <Eye className="w-3.5 h-3.5" />{coach.view_count || 0} צפיות
                        </span>
                      </div>
                      {coach.description && (
                        <p className="text-sm text-gray-600 mt-2 line-clamp-1">{coach.description}</p>
                      )}
                      <div className="flex items-center gap-1 mt-2">
                        {Array.from({ length: 5 }, (_, i) => (
                          <Star key={i} className={`w-3.5 h-3.5 ${i < Math.round(coach.average_rating) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
                        ))}
                        <span className="text-xs text-gray-400 mr-1">({coach.total_reviews})</span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-wrap gap-2 sm:flex-col">
                      <Link to={`/coaches/${coach.id}`}>
                        <Button variant="outline" size="sm" className="border-blue-400 text-blue-600">
                          <Eye className="w-4 h-4 ml-1" /> צפה
                        </Button>
                      </Link>
                      {coach.status !== 'approved' && (
                        <Button size="sm" className="bg-green-500 hover:bg-green-600 text-white" onClick={() => updateStatus(coach.id, 'approved')} data-testid={`approve-${coach.id}`}>
                          <CheckCircle className="w-4 h-4 ml-1" /> אשר
                        </Button>
                      )}
                      {coach.status !== 'rejected' && (
                        <Button size="sm" variant="outline" className="border-orange-400 text-orange-600" onClick={() => updateStatus(coach.id, 'rejected')} data-testid={`reject-${coach.id}`}>
                          <XCircle className="w-4 h-4 ml-1" /> דחה
                        </Button>
                      )}
                      <Button size="sm" variant="outline" className="border-red-400 text-red-600" onClick={() => deleteCoach(coach.id, coach.full_name)} data-testid={`delete-${coach.id}`}>
                        <Trash2 className="w-4 h-4 ml-1" /> מחק
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCoachesPage;
